/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import * as _ from 'lodash'

import { Button, CloseBtn } from '@styles/button.style'
import { RoundTabWrap, TotalTabWrap } from '@styles/base.style'
import { SelectedLeague } from '@views/Matchup/MatchField/MatchTab/index.style'
import { getLogoShieldIco } from '@views/Popup/MatchupFilter'
import { PAGE_TYPE } from '@interface/common'
import MatchUpList from '@views/Matchup/MatchUpList'
import useRootStore from '@store/useRootStore'
import { useOnMount } from '@utils/reactExt'
import { isMobile } from 'react-device-detect'
import { UserPickMatchList } from '@views/Home/UserPick/UserPickMatchList'
import { UserPickWriteButton } from '@views/Home/UserPick/Components/UserPickWriteButton'
import { FROM_VIEW_TYPE } from '@v2/constants/common'
import { ALL_MEMBER_USER_PICK_KEY } from '@constants/index'
import { Schema$MatchupFilter } from '@interface/matchup'
import { DetailTab } from '@views/Matchup/Tab/DetailTab'

interface MatchupFilterPageProps {
  onClose: () => void
}

export const MatchUpFilterPage: React.FC<MatchupFilterPageProps> = observer((props: MatchupFilterPageProps) => {
  const { onClose } = props
  const { matchupStore, userPickStore, globalStore } = useRootStore()
  const { matchupFilterList, tournamentFilterList, filteredTracks, pageType, filteredSelectedDetailTabs } = matchupStore
  const quickLeagueScrollRef = React.createRef<HTMLDivElement>()
  const [userPickMatchupFilters, setUserPickMatchupFilters] = useState<Schema$MatchupFilter[]>([])
  const isInitialDetailTabsMount = useRef(true)
  const scoreModeEnabled = globalStore.getScoreModeEnabled()

  const pageSize = 20
  const currentMatchupFilters = pageType === PAGE_TYPE.USER_PICK ? userPickMatchupFilters : matchupFilterList

  const tabQuickLeagueContents = _.map(tournamentFilterList, (tournamentId) => {
    const matchFilter = _.find(currentMatchupFilters, filter => filter.TournamentID === tournamentId)
    if (!matchFilter) {
      return (<Fragment/>)
    }
    const { emblemTournament, TournamentID, TournamentName } = matchFilter

    return (
      <SelectedLeague key={`quick-selected-${TournamentID}`}>
        <img src={emblemTournament} alt="cate" onError={getLogoShieldIco}
             className="quick-selected-league-emblem"/>
        <span className="quick-selected-league-name">
        {TournamentName}
        </span>
        <Button className="quick-selected-league-btn--wrapper" onClick={() => onClickLeagueDelete(TournamentID)} aria-label="닫기">
          <div css={CloseBtn} className="quick-selected-league-btn"/>
        </Button>
      </SelectedLeague>
    )
  })

  const onClickLeagueDelete = (tournamentId): void => {
    matchupStore.removeTournamentFilterListByTournamentId(tournamentId)
    quickLeagueScrollRef.current.scrollTo(0, 0)
  }

  useOnMount(() => {
    matchupStore.clearDetailTrack()
  })

  useEffect(() => {
    if (_.isEmpty(tournamentFilterList)) {
      onClose()
      return
    }

    if (pageType === PAGE_TYPE.USER_PICK) {
      userPickStore.setFilteredTargetSportId(undefined)
      userPickStore.fetchUserPickFilteredMatches(true).catch(console.error)
      return
    }

    let paramPageType = PAGE_TYPE.MATCHUP_FILTER
    if (pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER) {
      paramPageType = PAGE_TYPE.MATCHUP_RESULT_FILTER
    }
    matchupStore.fetchFilteredTournamentTracks({
      limit: pageSize,
      pageType: paramPageType,
      reset: true,
    }).catch(console.error)

  }, [tournamentFilterList])

  // SCORE 리그필터: 진행중/종료 탭 활성화 상태 변경 시 경기 목록 재조회
  // MobX observable 배열은 참조가 동일할 수 있어, 직렬화한 값을 의존성으로 사용
  const filteredDetailTabsKey = JSON.stringify(filteredSelectedDetailTabs ?? [])
  useEffect(() => {
    if (pageType !== PAGE_TYPE.MATCHUP_RESULT && pageType !== PAGE_TYPE.MATCHUP_RESULT_FILTER) return
    if (_.isEmpty(tournamentFilterList)) return
    if (isInitialDetailTabsMount.current) {
      isInitialDetailTabsMount.current = false
      return
    }
    matchupStore.fetchFilteredTournamentTracks({
      limit: pageSize,
      pageType: PAGE_TYPE.MATCHUP_RESULT_FILTER,
      reset: true,
      isDetailTabChange: true,
    }).catch(console.error)
  }, [filteredDetailTabsKey, scoreModeEnabled])

  useEffect(() => {
    if (pageType === PAGE_TYPE.USER_PICK) {
      setUserPickMatchupFilters([...matchupFilterList])
    }
  }, [])

  return (
    <Fragment>
      <RoundTabWrap
        ref={quickLeagueScrollRef}
        key="MatchupFilter:LeagueRoundTab"
        className={isMobile ? 'filter' : 'filter pc-filter'}
      >
        {tabQuickLeagueContents}
      </RoundTabWrap>

      {(pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER)
        && scoreModeEnabled && (
        <TotalTabWrap childFixed noHeader round style={{ marginTop: 6 }}>
          <DetailTab useFiltered={true}/>
        </TotalTabWrap>
      )}

      {pageType === PAGE_TYPE.USER_PICK && (
        <UserPickMatchList
          userPromiseIds={[ALL_MEMBER_USER_PICK_KEY]}
          className="userPickFilterList"
          pageType={PAGE_TYPE.USER_PICK_MATCH_FILTER}
        />
      )}
      {pageType !== PAGE_TYPE.USER_PICK && (
        <MatchUpList key="MatchupFilter:MatchUpList"
                     pageType={(pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER)
                       ? PAGE_TYPE.MATCHUP_RESULT_FILTER : PAGE_TYPE.MATCHUP_FILTER}
                     tracks={filteredTracks}/>
      )}

      {pageType !== PAGE_TYPE.MATCHUP_RESULT && (
        <UserPickWriteButton className={!isMobile && 'pc-filter-write-btn'} fromView={FROM_VIEW_TYPE.MATCHUP_FILTER}/>
      )}
    </Fragment>
  )
})
